import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Inject,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { MatSelectChange } from '@angular/material/select';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { Map, OrderedMap } from 'immutable';
import {
	BehaviorSubject,
	combineLatest,
	debounceTime,
	distinctUntilChanged,
	filter,
	firstValueFrom,
	merge,
	Observable,
	of,
	switchMap,
	takeUntil,
	tap,
	withLatestFrom,
} from 'rxjs';

import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { DocHintKeys } from '../../../ui/components/app-doc-hints/app-doc-hints.component';
import { ValidationErrorKeys } from '../../../ui/form/validation-erros-keys';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import {
	AccessConfigEntity,
	AccessType,
} from '../../domain/exposition-access-config.entity';
import {
	ColumnConfigErrorKey,
	ColumnConfigListError,
} from '../../domain/exposition-column-config-list.entity';
import { ExpositionColumnConfigEntity } from '../../domain/exposition-column-config.entity';
import { ExpositionConsumerEntity } from '../../domain/exposition-consumer.entity';
import {
	ExpositionDiagnosticError,
	ExpositionErrorKey,
} from '../../domain/exposition-diagnostic-errors.entity';
import { ExpositionProvider } from '../../providers/exposition.provider';
import { ExpositionsSelector } from '../../store';
import { ExpositionColumnsConfigComponent } from '../exposition-columns-config/exposition-columns-config.component';
import { SelectedConsumer } from './access-consumer-mapping-popup/access-consumer-item.entity';
import { AccessConsumerMappingPopupComponent } from './access-consumer-mapping-popup/access-consumer-mapping-popup.component';
import {
	AccessMetadataFormControls,
	ExpositionAccessMetadataForm,
} from './exposition-access-metadata-form';
import { ExpositionConsumerListComponent } from './exposition-consumers-list/exposition-consumer-list.component';

@Component({
	selector: 'app-exposition-access-config',
	templateUrl: './exposition-access-config.component.html',
	styleUrls: ['./exposition-access-config.component.scss'],
	providers: [ExpositionAccessMetadataForm],
	changeDetection: ChangeDetectionStrategy.OnPush,
	inputs: ['accessConfig', 'isInStepperMode'],
})
export class ExpositionAccessConfigComponent
	extends DcBaseComponent
	implements OnInit, AfterViewInit
{
	protected readonly ValidationErrorKeys = ValidationErrorKeys;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly DocHintKeys = DocHintKeys;
	public accessColumnsActions: Array<PanelAction> = [
		{
			order: 1,
			command: (): Promise<void> => this.updateIsActive(true),
			iconName: '',
			description: $localize`:@@expositions.columns.config.action.activate:`,
			typed: 'grouped-context-action',
		},
		{
			order: 2,
			command: (): Promise<void> => this.updateIsActive(false),
			iconName: '',
			description: $localize`:@@expositions.columns.config.action.disable:`,
			typed: 'grouped-context-action',
		},
		{
			order: 3,
			command: (): Promise<void> => this.hashSelectedColumns(),
			iconName: '',
			description: $localize`:@@expositions.columns.config.action.hash:`,
			typed: 'grouped-context-action',
		},
	];
	public consumersActions: Array<PanelAction> = [];

	private readonly isOverlayInfoOpenSubject = new BehaviorSubject<boolean>(
		false
	);
	private readonly accessConfigSubject =
		new BehaviorSubject<AccessConfigEntity>(AccessConfigEntity.build());
	private readonly colConfigListErrorsSubject = new BehaviorSubject<
		Map<ColumnConfigErrorKey, ColumnConfigListError>
	>(Map());
	private readonly isMetadataValidSubject = new BehaviorSubject<boolean>(false);

	@Output()
	public hasValidityChanged = new EventEmitter<boolean>();

	@ViewChild(ExpositionConsumerListComponent)
	private readonly expositionConsumerList!: ExpositionConsumerListComponent;

	@ViewChild(ExpositionColumnsConfigComponent)
	private readonly expositionColumnsConfig!: ExpositionColumnsConfigComponent;

	public Appearance: MatFormFieldAppearance = 'legacy';
	protected readonly AccessMetadataFormControls = AccessMetadataFormControls;
	protected readonly AccessType = AccessType;

	public DcIcons = DcIcons;
	public vo$: Observable<{
		accessConfig: AccessConfigEntity;
		listErrors: Map<ColumnConfigErrorKey, ColumnConfigListError>;
		isOverlayInfoOpened: boolean;
		canNotCreateOpenAccess: boolean;
	}>;

	protected readonly ColumnConfigErrorKey = ColumnConfigErrorKey;
	public constructor(
		private readonly expositionsSelector: ExpositionsSelector,
		public readonly accessMetadataForm: ExpositionAccessMetadataForm,
		private readonly modalService: AppModalService,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons,
		private readonly expositionProvider: ExpositionProvider
	) {
		super();
		this.cmpId = 'exposition-access-config';

		const isInStepperMode$ = merge(
			of(false),
			this.toObservable<boolean>('isInStepperMode')
		);

		this.toObservable<AccessConfigEntity>('accessConfig')
			.pipe(
				takeUntil(this.onDestroy$),
				tap((config) => {
					const updatedEls = OrderedMap(
						config.columnsConfig.elements
							.entrySeq()
							.sort(([, colA], [, colB]) => {
								if (colA.position === -1) {
									return 1;
								}
								if (colB.position === -1) {
									return -1;
								}
								return colA.position - colB.position;
							})
					);

					const updatedAccessConfig = config.updateColumnsConfig(
						config.columnsConfig.setElements(updatedEls)
					);
					this.accessConfigSubject.next(updatedAccessConfig);
				}),
				filter((access) => access.id !== ''),
				distinctUntilChanged(),
				tap((config) => {
					this.accessMetadataForm.populate(config);
					if (config.label) {
						this.isMetadataValidSubject.next(true);
					}
				})
			)
			.subscribe();

		const canNotCreateOpenAccess$ = isInStepperMode$.pipe(
			switchMap((isInStepperMode) => {
				if (isInStepperMode) {
					return of(false);
				}
				return this.toObservable<AccessConfigEntity>('accessConfig').pipe(
					switchMap((access) =>
						this.expositionsSelector.hasConfiguredOpenAccess$(access.id)
					)
				);
			})
		);

		this.vo$ = combineAll({
			accessConfig: this.accessConfigSubject,
			listErrors: this.colConfigListErrorsSubject,
			isOverlayInfoOpened: this.isOverlayInfoOpenSubject,
			canNotCreateOpenAccess: canNotCreateOpenAccess$,
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();

		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => this.bootstrapIcons())
			)
			.subscribe();
	}

	public ngAfterViewInit(): void {
		super.ngAfterViewInit();

		this.accessMetadataForm.form.valueChanges
			.pipe(
				debounceTime(100),
				withLatestFrom(this.accessConfigSubject),
				takeUntil(this.onDestroy$),
				tap(([, accessConfig]) => {
					const isValid = this.accessMetadataForm.form.valid;
					this.isMetadataValidSubject.next(isValid);
					const metadata = this.accessMetadataForm.extract();

					if (isValid) {
						const updated = accessConfig.updateMetadata(
							metadata.label ?? '',
							metadata.description ?? '',
							metadata.isActive ?? false
						);
						this.accessConfigSubject.next(updated);
					}
				})
			)
			.subscribe();

		combineLatest([
			this.colConfigListErrorsSubject,
			this.isMetadataValidSubject,
			this.accessConfigSubject,
		])
			.pipe(
				takeUntil(this.onDestroy$),
				tap(([errors, isMetadataValid, accessConfig]) => {
					const isAccessTypeValid =
						accessConfig.type === AccessType.Limited
							? accessConfig.consumers.elements.size !== 0
							: true;

					if (!isMetadataValid) {
						this.expositionProvider.addError(
							ExpositionDiagnosticError.build({
								key: ExpositionErrorKey.AccessMetadataMissingFields,
								type: 'critic',
								message: $localize`:@@exposition.diagnosticError.accessMetadataMissingFields:`,
							})
						);
					} else {
						this.expositionProvider.removeError(
							ExpositionErrorKey.AccessMetadataMissingFields
						);
					}

					if (!isAccessTypeValid) {
						this.expositionProvider.addError(
							ExpositionDiagnosticError.build({
								key: ExpositionErrorKey.NoSelectAccessConsumers,
								type: 'critic',
								message: $localize`:@@exposition.diagnosticError.noSelectAccessConsumers:`,
							})
						);
					} else {
						this.expositionProvider.removeError(
							ExpositionErrorKey.NoSelectAccessConsumers
						);
					}

					if (errors.has(ColumnConfigErrorKey.NoActiveColumns)) {
						this.expositionProvider.addError(
							ExpositionDiagnosticError.build({
								key: ExpositionErrorKey.NoActiveAccessColumns,
								type: 'critic',
								message: $localize`:@@exposition.diagnosticError.noActiveAccessColumns:`,
							})
						);
					} else {
						this.expositionProvider.removeError(
							ExpositionErrorKey.NoActiveAccessColumns
						);
					}

					this.hasValidityChanged.emit(
						isMetadataValid && isAccessTypeValid && !errors.size
					);
				})
			)
			.subscribe();
	}

	public async removeConsumers(ids: Array<string>): Promise<void> {
		const accessConfig = await firstValueFrom(this.accessConfigSubject);
		const updated = accessConfig.updateConsumers(
			accessConfig.consumers.removeConsumers(ids)
		);
		this.accessConfigSubject.next(updated);
	}

	public async changeFilter(filter: string): Promise<void> {
		const accessConfig = await firstValueFrom(this.accessConfigSubject);
		const updated = accessConfig.updateFilter(filter);
		this.accessConfigSubject.next(updated);
	}

	public async changeIsAccessLimited($event: MatSelectChange): Promise<void> {
		const accessConfig = await firstValueFrom(this.accessConfigSubject);
		const updated = accessConfig.updateAccessType($event.value);
		this.accessConfigSubject.next(updated);
	}

	public modifyColumnConfigValidity(
		errors: Map<ColumnConfigErrorKey, ColumnConfigListError>
	): void {
		this.colConfigListErrorsSubject.next(errors);
	}

	public getAccessConfig(): AccessConfigEntity {
		if (!this.expositionColumnsConfig) {
			throw new Error('Exposition Columns Config Cmp no where to be found !');
		}
		const previousAccess = this.accessConfigSubject.value;
		return previousAccess.updateColumnsConfig(
			this.expositionColumnsConfig.getUpdatedList()
		);
	}

	private async addConsumers(): Promise<void> {
		const accessConfig = await firstValueFrom(this.accessConfigSubject);
		const initiallySelected: Map<number, SelectedConsumer> = Map(
			accessConfig.consumers.elements
				.toList()
				.toArray()
				.map((consumer) => [
					consumer.id,
					{
						id: consumer.id,
						name: consumer.name,
						isGroup: consumer.isGroup,
						avatar: consumer.avatar,
					} as SelectedConsumer,
				])
		);

		const modalRef = this.modalService.openPopup<{
			selectedConsumers: Map<number, SelectedConsumer>;
		}>(
			AccessConsumerMappingPopupComponent,
			{
				title: $localize`:i18n=@@exposition.access.consumer.mapping.popup.header:`,
				subTitle: '',
				width: '85%',
				height: '90%',
				selectedConsumers: initiallySelected,
			},
			{
				disableClose: true,
			}
		);
		const modalResult = await firstValueFrom(modalRef.afterClosed());
		if (!modalResult) {
			return;
		}
		const { selectedConsumers } = modalResult;

		const consumers: Map<string, ExpositionConsumerEntity> = Map(
			selectedConsumers
				.toList()
				.toArray()
				.map((consumer: SelectedConsumer) => [
					`${consumer.id}`,
					ExpositionConsumerEntity.build({
						id: consumer.id,
						name: consumer.name,
						isGroup: consumer.isGroup,
					}),
				])
		);

		const updated = accessConfig.updateConsumers(
			accessConfig.consumers.setElements(consumers)
		);
		this.accessConfigSubject.next(updated);
	}

	private async updateIsActive(isActive: boolean): Promise<void> {
		const accessConfig = await firstValueFrom(this.accessConfigSubject);
		const selection =
			(await this.expositionColumnsConfig.getSelectedColumns()) as Array<ExpositionColumnConfigEntity>;
		const selectedIds = selection.map((s) => s.id);
		const updated = accessConfig.updateColumnsConfig(
			accessConfig.columnsConfig.updateIsActive(selectedIds, isActive)
		);
		this.accessConfigSubject.next(updated);
	}

	private async hashSelectedColumns(): Promise<void> {
		const accessConfig = await firstValueFrom(this.accessConfigSubject);
		const selection =
			(await this.expositionColumnsConfig.getSelectedColumns()) as Array<ExpositionColumnConfigEntity>;
		const selectedIds = selection
			.map((s) => s.id)
			.filter((s) => accessConfig.columnsConfig.hashedColumns.includes(s));
		if (selectedIds.length) {
			const updated = accessConfig.updateColumnsConfig(
				accessConfig.columnsConfig.updateHash(selectedIds, true)
			);
			this.accessConfigSubject.next(updated);
		}
	}

	private async removeSelectedConsumers(): Promise<void> {
		const accessConfig = await firstValueFrom(this.accessConfigSubject);
		const selection =
			(await this.expositionConsumerList.getSelectedConsumers()) as Array<ExpositionConsumerEntity>;
		const updated = accessConfig.updateConsumers(
			accessConfig.consumers.removeConsumers(selection.map((s) => `${s.id}`))
		);
		this.accessConfigSubject.next(updated);
	}

	private async bootstrapIcons(): Promise<void> {
		const actions = [ActionIcons.AddExpositionAccess];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);
		this.consumersActions = [
			{
				order: 0,
				command: this.addConsumers.bind(this),
				iconName: actionMap.get(ActionIcons.AddExpositionAccess)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@exposition.access.config.add-consumers:`,
				typed: '',
			},
			{
				order: 1,
				command: this.removeSelectedConsumers.bind(this),
				iconName: 'delete',
				isSvg: false,
				typed: 'plain-context-action',
				description: $localize`:@@expositions.access.config.remove:`,
			},
		];
	}

	public async toggleOverlayInfo(): Promise<void> {
		const overlayState = await firstValueFrom(this.isOverlayInfoOpenSubject);
		if (overlayState) {
			return this.isOverlayInfoOpenSubject.next(false);
		}
		this.isOverlayInfoOpenSubject.next(true);
	}

	public closeOverlayInfo(): void {
		this.isOverlayInfoOpenSubject.next(false);
	}
}
